/**
 * How a clue fits its cell. Clue cells are tiny: each clue starts at its token size, and
 * Archivo's width axis condenses (84 → 62) before the size drops, never below the floor
 * (`--clue-min`). At the floor, a clue that still doesn't fit is set tight (leading,
 * padding, tracking).
 *
 * If it still doesn't fit, it shows a short cue rather than being cut off at the cell's
 * edge (`cue`, which the board always asks for, UX-031); "Larger clues" (Settings) raises
 * the floor, so cues come sooner. A cue is as many of its first words as fit, then "…"
 * ("Of the…"), or, if even the first word won't, that word cut short ("Astron…").
 * (Hyphenation can't help: browsers don't hyphenate capitalised words, and a clue's
 * long word is usually its first.) The full clue is always in the clue bar
 * (20px) and in the cell's accessible name.
 *
 * The DOM side (Board.tsx) is a `ClueProbe`: it applies a setting and says whether the
 * text overflows. Measured at the selected clue's weight, so selecting a clue never
 * pushes it out of its cell.
 */

export interface ClueSetting {
	/** Font size, px. */
	size: number;
	/** Archivo's width axis. */
	wdth: number;
	/** The last resort at the floor: tighter leading, padding and tracking. */
	tight: boolean;
	/** The short cue shown in place of the clue, or null for the whole clue. */
	cue: string | null;
}

export interface ClueProbe {
	apply(setting: ClueSetting): void;
	/** Does the clue, as last applied, overflow its cell? */
	over(): boolean;
}

export interface ClueFitOptions {
	/** The starting size (the cell's share, clamped to the floor and the max). */
	size0: number;
	/** Never smaller than this. */
	floor: number;
	wdth0: number;
	wdthMin: number;
	/** Larger clues: fall back to a short cue rather than overflow at the floor. */
	cue: boolean;
}

const WDTH_STEP = 4;
const SIZE_STEP = 0.25;

/** Words a cue shouldn't end on ("Claim, or…"). */
const SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'into', 'of', 'on', 'or', 'the', 'to', 'with']);

/** The ellipsis that ends a cue. */
export const CUE_END = '…';

/**
 * The cues to try for `text`, longest first: its first n−1, n−2, … 1 words (never
 * ending on a small word like "of", unless the cue is nothing but), then the first word
 * cut one letter at a time (down to one letter). Trailing punctuation goes before the "…".
 */
export function cueCandidates(text: string): string[] {
	const words = text.trim().split(/\s+/).filter(Boolean);
	const out: string[] = [];
	const add = (s: string) => {
		const t = s.replace(/[\s,;:.!?'"’”(–—-]+$/u, '');
		if (t && !out.includes(t + CUE_END)) out.push(t + CUE_END);
	};
	const small = (w: string) => SMALL_WORDS.has(w.toLowerCase().replace(/[^a-z]/g, ''));
	for (let k = words.length - 1; k >= 1; k--) {
		const head = words.slice(0, k);
		// "Organ…", not "Organ of…": a cue doesn't end on a small word (unless that's all it has).
		if (small(head[k - 1]!) && !head.every(small)) continue;
		add(head.join(' '));
	}
	const first = [...(words[0] ?? '')];
	for (let n = first.length - 1; n >= 1; n--) add(first.slice(0, n).join(''));
	return out;
}

/**
 * Fits one clue: the full clue if it fits at or above the floor, else (with `cue`) the
 * longest cue that does. Leaves the probe at the returned setting. Without `cue`, a clue
 * that can't fit ends at the floor, tight (as before Larger clues).
 */
export function fitClue(text: string, probe: ClueProbe, o: ClueFitOptions): ClueSetting {
	const floor = o.floor;
	const attempt = (cue: string | null): ClueSetting => {
		const s: ClueSetting = { size: Math.max(floor, o.size0), wdth: o.wdth0, tight: false, cue };
		probe.apply(s);
		while (probe.over() && s.wdth > o.wdthMin) {
			s.wdth = Math.max(o.wdthMin, s.wdth - WDTH_STEP);
			probe.apply(s);
		}
		while (probe.over() && s.size > floor) {
			s.size = Math.max(floor, s.size - SIZE_STEP);
			probe.apply(s);
		}
		if (probe.over()) {
			s.tight = true;
			probe.apply(s);
		}
		return s;
	};
	const full = attempt(null);
	if (!o.cue || !probe.over()) return full;
	let last = full;
	for (const cue of cueCandidates(text)) {
		last = attempt(cue);
		if (!probe.over()) return last;
	}
	return last;
}
